import { useMemo } from 'react';
import type { Table } from '@tanstack/react-table';
import type { FilterConfig, EditableCellConfig } from '../types';

interface FacetedValue {
  value: string;
  label: string;
  count: number;
}

/**
 * Custom hook for computing faceted values for select filters
 * Extracts shared logic between FilterValuesPanel and FilterValuesList
 */
export function useFacetedValues<T>(
  hoveredOption: string | null,
  table: Table<T>,
  filterConfig: FilterConfig<T>,
  columnConfig: EditableCellConfig<T> | null | undefined
): FacetedValue[] | null {
  return useMemo(() => {
    if (!hoveredOption || !columnConfig) return null;
    
    const column = table.getColumn(hoveredOption);
    if (!column) return null;

    const filterType = filterConfig[hoveredOption as keyof FilterConfig<T>];
    if (filterType !== 'select') return null;

    // Get faceted unique values from the table
    const faceted = column.getFacetedUniqueValues();
    
    // If column has select options, use those with counts
    if (columnConfig.selectOptions) {
      // For array-valued columns (like labels), faceted unique values don't work
      // because TanStack Table facets the whole array as a single value.
      // Compute counts manually by iterating visible rows.
      const isArrayColumn = columnConfig.accessorKey === 'labels';
      if (isArrayColumn) {
        const rows = table.getPreFilteredRowModel().rows;
        const countMap = new Map<string, number>();
        for (const row of rows) {
          const cellValue = row.getValue(hoveredOption) as Array<{ id?: string }> | null;
          if (Array.isArray(cellValue)) {
            for (const item of cellValue) {
              if (item.id) {
                countMap.set(item.id, (countMap.get(item.id) || 0) + 1);
              }
            }
          }
        }
        return columnConfig.selectOptions.map(option => ({
          value: option.value,
          label: option.label,
          count: countMap.get(option.value) || 0,
        }));
      }

      // Faceted unique-value keys are the raw cell values (e.g. booleans for a
      // subsidised column), while selectOptions use string values ("true"/"false").
      // Stringify the keys so non-string cell values still match their option.
      const stringCounts = new Map<string, number>();
      for (const [key, count] of faceted.entries()) {
        const k = String(key);
        stringCounts.set(k, (stringCounts.get(k) || 0) + count);
      }
      return columnConfig.selectOptions.map(option => ({
        value: option.value,
        label: option.label,
        count: stringCounts.get(option.value) || 0,
      }));
    }

    // Otherwise, create options from faceted values
    return Array.from(faceted.entries())
      .filter(([value]) => value != null)
      .map(([value, count]) => ({
        value: String(value),
        label: String(value),
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [hoveredOption, table, filterConfig, columnConfig]);
}